import { computed, observable, makeObservable, actionBound } from "mobx";
import OrderTableColumnsType from "../../enum/OrderTableColumnsType";
import {
    OrderTableColumn,
    getOrderTableColumnsStorage,
    setOrderTableColumnsStorage,
    UnOperationOrderTableColumnsType,
} from "./utils/orderTableColumns";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderItemColumnSettingStore from "../OrderItemColumnSettingStore";
import { sortBy, cloneDeep, chunk, xor } from "lodash";
import {
    LogisticsAgingPredictOrderParams,
    LogisticsAgingPredictResult,
    getLogisticsAgingPredicts,
} from "@/services/api/LogisticsAgingPredicts";
import GlobalStore from "@/store";
import { uuid } from "@/utils";
import PremiumValidator from "@/services/PremiumValidator";
import { DyPlatformShopTypes } from "@/utils/ShopUtils";
import to from "@/utils/to";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderTab from "../../enum/OrderTab";
import FrequentlyUsedField from "@/enum/FrequentlyUsedField";
import { UniversalOrderSettingType } from "@/services/api/universalOrderSetting";
import { getOrderSearchPanelSettingList } from "@/services/api/orderSearchPanelSetting";
import { allFrequentlyUsedFields, defaultFrequentlyUsedFields } from "./utils/orderSearchPanel";
import ShopType from "@/enum/ShopType";
import { getKsRecommendLogistics, KsRecommendedExpress } from "@/services/api/kwaishop/logistics";
import { getRealOrderOriginalId } from "@/utils/OrderUtils";
import {
    getQuickTimeSelectConfigs,
    QuickTimeSelectConfig,
    updateQuickTimeSelectConfigSort,
} from "@/services/api/companySetting";
import { OperationKey } from "@/scenes/OrderManagement/components/BottomOperationBar/OtherOperationsButton/services/otherOperationDefinitions";

const FAVORITE_OPS_KEY_PREFIX = "order_mgmt_fav_ops";
const OTHER_OPERATION_KEY_SET = new Set(Object.values(OperationKey));

const buildFavoriteOpsStorageKey = (companyId: number) => {
    return `${FAVORITE_OPS_KEY_PREFIX}:company:${companyId}`;
};

const normalizeFavoriteKeys = (keys: unknown): OperationKey[] => {
    if (!Array.isArray(keys)) return [];

    return keys.filter((key): key is OperationKey => OTHER_OPERATION_KEY_SET.has(key as OperationKey));
};

const COLUMN_WIDTH_CONFIG_KEY = "__COLUMN_WIDTH_CONFIG__";

/**
 * 列宽配置接口
 */
export interface ColumnWidthConfig {
    key: OrderTableColumnsType;
    width: number;
}

/**
 * 管理订单表格相关状态的存储类。
 */
export default class OrderTableStorageStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            orderTableColumns: observable,
            isShowLogisticsAgingPredict: observable,
            logisticsAgingPredictMapping: observable,
            logisticsAgingPredictLoading: observable,
            columnWidthMap: observable,
            initColumnWidthConfigs: actionBound,
            setColumnWidth: actionBound,
            switchShowLogisticsAgingPredict: actionBound,
            sortedColumns: computed,
            orderTableColumnMap: computed,
            visibleColumnCount: computed,
            getShopOrderTableColumns: actionBound,
            toggleOrderTableColumnsVisible: actionBound,
            toggleOrderTableColumnsPosition: actionBound,
            dargOrderTableColumnsPosition: actionBound,
            isShowAllOrderDetails: observable,
            showOrderDetailMapping: observable,
            showAllOrderDetails: actionBound,
            hideAllOrderDetails: actionBound,
            batchClearShowOrderDetails: actionBound,
            toggleShowOrderDetail: actionBound,
            loadLogisticsAgingPredictMapping: actionBound,
            ksLogisticsRecommendMapping: observable,
            loadKsLogisticsRecommendMapping: actionBound,
            dispose: actionBound,
            orderSearchPanelLoading: observable,
            frequentlyUsedFields: observable,
            unFrequentlyUsedFields: computed,
            loadOrderSearchPanelSettingList: actionBound,
            saveOrderSearchPanelSettingList: actionBound,
            quickTimeSelectConfigs: observable,
            quickTimeSelectConfigMapping: computed,
            quickTimeSelectConfigLoading: observable,
            favoriteOperationKeysCache: observable,
            favoriteOperationKeysStoredCache: observable,
            initFavoriteOperationKeys: actionBound,
            setFavoriteOperationKeys: actionBound,
            toggleFavoriteOperationKey: actionBound,
            ensureQuickTimeSelectConfigs: actionBound,
            loadQuickTimeSelectConfigs: actionBound,
            updateQuickTimeSelectConfigSort: actionBound,
        });
        this.initFavoriteOperationKeys();
        this.initColumnWidthConfigs();
    }

    orderTableColumns: OrderTableColumn[] = [];

    isShowLogisticsAgingPredict: boolean;

    logisticsAgingPredictMapping = new Map<number, LogisticsAgingPredictResult>();

    logisticsAgingPredictLoading = false;

    orderItemColumnSetting = new OrderItemColumnSettingStore();

    columnWidthMap = new Map<OrderTableColumnsType, number>();

    /**
     * 初始化列宽配置
     */
    initColumnWidthConfigs() {
        try {
            const saved = localStorage.getItem(COLUMN_WIDTH_CONFIG_KEY);
            if (saved) {
                const configs: ColumnWidthConfig[] = JSON.parse(saved);
                if (Array.isArray(configs)) {
                    configs.forEach(({ key, width }) => {
                        if (width >= 150) {
                            this.columnWidthMap.set(key, width);
                        }
                    });
                }
            }
        } catch (error) {
            console.error("解析列宽配置失败", error);
        }
    }

    /**
     * 保存所有列宽配置到 localStorage
     */
    private saveColumnWidthConfigsToStorage() {
        try {
            const configs: ColumnWidthConfig[] = [];
            this.columnWidthMap.forEach((width, key) => {
                configs.push({ key, width });
            });
            localStorage.setItem(COLUMN_WIDTH_CONFIG_KEY, JSON.stringify(configs));
        } catch (error) {
            console.error("保存列宽配置失败", error);
        }
    }

    /**
     * 设置指定列的宽度
     */
    setColumnWidth(columnKey: OrderTableColumnsType, width: number) {
        const validWidth = Math.max(150, width);
        this.columnWidthMap.set(columnKey, validWidth);
        this.saveColumnWidthConfigsToStorage();
    }

    switchShowLogisticsAgingPredict(checked: boolean) {
        this.isShowLogisticsAgingPredict = checked;
    }

    get sortedColumns() {
        const visibleColumns = this.orderTableColumns.filter(({ visible }) => visible);
        const sortColumns = sortBy(visibleColumns, (column) => Number(column.index));

        return sortColumns;
    }

    get orderTableColumnMap() {
        const map = new Map<OrderTableColumnsType, OrderTableColumn>();

        this.orderTableColumns.forEach((orderTableColumn) => {
            map.set(orderTableColumn.type, orderTableColumn);
        });

        return map;
    }

    get visibleColumnCount() {
        return this.orderTableColumns.filter(({ visible }) => visible).length;
    }

    getShopOrderTableColumns(shop: ShopDTO | undefined) {
        this.orderTableColumns = getOrderTableColumnsStorage(shop);

        return this.orderTableColumns;
    }

    toggleOrderTableColumnsVisible(shop: ShopDTO | undefined, orderTableColumnTypes: OrderTableColumnsType[]) {
        const newOrderTableColumns: OrderTableColumn[] = cloneDeep(this.orderTableColumns);

        newOrderTableColumns.forEach((column) => {
            if (orderTableColumnTypes.includes(column.type) || UnOperationOrderTableColumnsType.includes(column.type)) {
                column.visible = true;
            } else {
                column.visible = false;
            }
        });

        this.orderTableColumns = newOrderTableColumns;

        setOrderTableColumnsStorage(shop, newOrderTableColumns);
    }

    toggleOrderTableColumnsPosition(shop: ShopDTO | undefined, type: OrderTableColumnsType, isUp: boolean) {
        const index = this.orderTableColumns.findIndex((orderTableColumn) => orderTableColumn.type === type);

        if (index === -1) return;

        const newOrderTableColumns = [];

        const predictionIndex = isUp ? index - 1 : index + 1;

        this.orderTableColumns.forEach((column, i) => {
            if (i === index) {
                newOrderTableColumns[predictionIndex] = { ...column, index: predictionIndex };
            } else if (i === predictionIndex) {
                newOrderTableColumns[index] = { ...column, index };
            } else {
                newOrderTableColumns[i] = { ...column, index: i };
            }
        });

        this.orderTableColumns = newOrderTableColumns;

        setOrderTableColumnsStorage(shop, newOrderTableColumns);
    }

    dargOrderTableColumnsPosition(shop: ShopDTO | undefined, target: number, origin: number) {
        const newOrderTableColumns: OrderTableColumn[] = cloneDeep(this.orderTableColumns);

        const removed = newOrderTableColumns.splice(origin, 1);

        newOrderTableColumns.splice(target, 0, removed[0]);

        newOrderTableColumns.forEach((column, index) => {
            column.index = index;
        });

        this.orderTableColumns = newOrderTableColumns;

        setOrderTableColumnsStorage(shop, newOrderTableColumns);
    }

    /**
     * 是否展开所有详情
     */
    isShowAllOrderDetails = false;

    /**
     * 订单的展开情况
     * 优先级高于isShowAllOrderDetails
     */
    showOrderDetailMapping = new Map<number, boolean>();

    /**
     * 批量操作订单展开
     */
    showAllOrderDetails() {
        this.isShowAllOrderDetails = true;
    }

    /**
     * 批量操作订单收缩
     */
    hideAllOrderDetails() {
        this.isShowAllOrderDetails = false;
    }

    /**
     * 批量操作删除订单收缩记录
     */
    batchClearShowOrderDetails(orderIds: number[]) {
        for (let i = 0; i < orderIds.length; i++) {
            const orderId = orderIds[i];

            this.showOrderDetailMapping.delete(orderId);
        }
    }

    /**
     * 切换操作订单展开
     */
    toggleShowOrderDetail(orderId: number) {
        const showOrderDetail = this.showOrderDetailMapping.get(orderId);

        if (this.isShowAllOrderDetails && typeof showOrderDetail === "undefined") {
            this.showOrderDetailMapping.set(orderId, false);
        } else {
            this.showOrderDetailMapping.set(orderId, !showOrderDetail);
        }
    }

    requestLogisticsAgingPredictId: string;

    async loadLogisticsAgingPredictMapping(
        globalStore: typeof GlobalStore,
        orders: OrderDTO[],
        shopId: number,
        tab: OrderTab,
    ) {
        try {
            const shop = globalStore.shopMapping.get(shopId);

            this.logisticsAgingPredictMapping.clear();

            if (tab !== OrderTab.PENDING_SHIPMENT) return;
            /** 无开启配送时效列 */
            if (!this.isShowLogisticsAgingPredict) return;
            /** 目前仅支持抖系平台 */
            if (!DyPlatformShopTypes.includes(shop.type)) return;
            /** 高级版限制 */
            const { isPremium } = await PremiumValidator.validateShopVersion(shopId, globalStore);
            if (!isPremium) return;

            this.logisticsAgingPredictLoading = true;

            const requestLogisticsAgingPredictId = uuid();
            this.requestLogisticsAgingPredictId = requestLogisticsAgingPredictId;

            await globalStore.waybillPrintStore.ensureShippingAddresses();

            const shippingAddresses = globalStore.waybillPrintStore.shippingAddresses;

            if (!shippingAddresses.length) return;

            /** 优先使用该店铺绑定的发货人，其次为收货人列表中的第一个收货人 */
            const ordersParams: LogisticsAgingPredictOrderParams[] = [];

            orders.forEach(({ id, originalId, mergeSplitStatus, shopId }) => {
                /** 避免是其他平台的订单 */
                if (!DyPlatformShopTypes.includes(globalStore.shopMapping.get(shopId)?.type)) return;

                ordersParams.push({ id, originalId, mergeSplitStatus: mergeSplitStatus });
            });

            if (!ordersParams.length) return;

            const shippingAddress =
                shippingAddresses.find(({ bindingShopIds }) => bindingShopIds?.includes(String(shopId))) ||
                shippingAddresses[0];

            const logisticsAgingPredictResults: LogisticsAgingPredictResult[] = [];
            const ordersParamChunks = chunk(ordersParams, 200);

            for (const ordersParamChunk of ordersParamChunks) {
                const [, results] = await to(
                    getLogisticsAgingPredicts({
                        shopId,
                        orders: ordersParamChunk,
                        shipperAddress: shippingAddress,
                    }),
                );

                logisticsAgingPredictResults.push(...(results || []));
            }

            /** 保证只设置最新的一次请求结果 */
            if (this.requestLogisticsAgingPredictId !== requestLogisticsAgingPredictId) return;

            if (!logisticsAgingPredictResults.length) return;

            const map = new Map<number, LogisticsAgingPredictResult>();

            logisticsAgingPredictResults.forEach((res) => {
                map.set(res.orderId, res);
            });

            this.logisticsAgingPredictMapping = map;
        } catch (e) {
            console.error(e);
        } finally {
            this.logisticsAgingPredictLoading = false;
        }
    }

    ksLogisticsRecommendMapping = new Map<string, KsRecommendedExpress[]>();

    async loadKsLogisticsRecommendMapping(
        globalStore: typeof GlobalStore,
        orders: OrderDTO[],
        shopId: number,
        tab: OrderTab,
    ) {
        const shop = globalStore.shopMapping.get(shopId);
        if (![ShopType.KWAI].includes(shop.type) || tab !== OrderTab.PENDING_SHIPMENT || !orders?.length) return;

        const orderOriginalIds = orders.map((order) => getRealOrderOriginalId(order));

        const chunks = chunk(orderOriginalIds, 200);
        const map = new Map<string, KsRecommendedExpress[]>();

        for (const chunk of chunks) {
            const res = await getKsRecommendLogistics(shop.id, chunk);

            res.forEach((res) => {
                map.set(res.orderOriginalId, res.recommendExpressList);
            });
        }

        this.ksLogisticsRecommendMapping = map;
    }

    /**
     * 销毁
     */
    dispose() {
        this.showOrderDetailMapping.clear();
    }

    // ---------------------搜索项设置------------------------------
    orderSearchPanelLoading = false;

    frequentlyUsedFields: FrequentlyUsedField[] = defaultFrequentlyUsedFields;

    get unFrequentlyUsedFields() {
        return xor(allFrequentlyUsedFields, this.frequentlyUsedFields);
    }

    async loadOrderSearchPanelSettingList() {
        try {
            this.orderSearchPanelLoading = true;

            const params = {
                companyId: this.globalStore.companyId,
                type: UniversalOrderSettingType.ORDER_SEARCH_PANEL,
            };

            await this.globalStore.orderSettingStore.fetchSettings(params);
            const newData = this.globalStore.orderSettingStore.scopeSettingsMap.get(
                this.globalStore.orderSettingStore.buildScopeKey(params),
            );

            const oldData = await getOrderSearchPanelSettingList();

            // 迁移数据
            if (oldData?.frequentlyUsedFields?.length && !newData?.settingContent) {
                const stringifiedData = JSON.stringify(oldData.frequentlyUsedFields);

                this.globalStore.orderSettingStore.saveSetting(
                    {
                        type: UniversalOrderSettingType.ORDER_SEARCH_PANEL,
                        settingContent: stringifiedData,
                    },
                    true,
                );

                console.log("迁移数据");
            }

            // 新接口中有数据，优先从新接口中获取
            if (newData?.settingContent) {
                const parsedData = JSON.parse(newData.settingContent) as FrequentlyUsedField;

                if (Array.isArray(parsedData)) {
                    this.frequentlyUsedFields = parsedData;
                    return;
                }
            }

            // 新接口没有数据，从旧接口中获取
            if (oldData?.frequentlyUsedFields?.length) {
                this.frequentlyUsedFields = oldData.frequentlyUsedFields;
            }
        } catch {
        } finally {
            this.orderSearchPanelLoading = false;
        }
    }

    async saveOrderSearchPanelSettingList(frequentlyUsedFields: FrequentlyUsedField[]) {
        const stringifiedData = JSON.stringify(frequentlyUsedFields);

        await this.globalStore.orderSettingStore.saveSetting({
            type: UniversalOrderSettingType.ORDER_SEARCH_PANEL,
            settingContent: stringifiedData,
        });

        this.frequentlyUsedFields = frequentlyUsedFields;
    }

    // --------------------- 结束 ------------------------------

    quickTimeSelectConfigs: QuickTimeSelectConfig[] = [];

    get quickTimeSelectConfigMapping() {
        return new Map(this.quickTimeSelectConfigs.map((it) => [it.id, it]));
    }

    quickTimeSelectConfigLoading = false;

    // ----------------------------

    favoriteOperationKeysCache: OperationKey[] = [];

    favoriteOperationKeysStoredCache: boolean = null;

    initFavoriteOperationKeys() {
        const storageKey = buildFavoriteOpsStorageKey(this.globalStore.companyId);

        try {
            const raw = localStorage.getItem(storageKey);
            const parsed = raw ? JSON.parse(raw) : [];
            const nextKeys = normalizeFavoriteKeys(parsed);

            this.favoriteOperationKeysCache = nextKeys;
            this.favoriteOperationKeysStoredCache = raw !== null;
        } catch (error) {
            this.favoriteOperationKeysCache = [];
            this.favoriteOperationKeysStoredCache = false;
        }
    }

    setFavoriteOperationKeys(keys: OperationKey[] = []) {
        const storageKey = buildFavoriteOpsStorageKey(this.globalStore.companyId);

        const nextKeys = normalizeFavoriteKeys(keys);

        this.favoriteOperationKeysCache = nextKeys;
        this.favoriteOperationKeysStoredCache = true;

        try {
            localStorage.setItem(storageKey, JSON.stringify(nextKeys));
        } catch (error) {
            // ignore
        }
    }

    toggleFavoriteOperationKey(key: OperationKey) {
        const currentKeys = this.favoriteOperationKeysCache || [];

        const index = currentKeys.indexOf(key);

        if (index >= 0) {
            const nextKeys = [...currentKeys];
            nextKeys.splice(index, 1);
            this.setFavoriteOperationKeys(nextKeys);
            return nextKeys;
        }

        const nextKeys = [...currentKeys, key];
        this.setFavoriteOperationKeys(nextKeys);
        return nextKeys;
    }

    async ensureQuickTimeSelectConfigs() {
        if (this.quickTimeSelectConfigs.length) return;

        return await this.loadQuickTimeSelectConfigs();
    }

    async loadQuickTimeSelectConfigs() {
        this.quickTimeSelectConfigLoading = true;

        try {
            this.quickTimeSelectConfigs = await getQuickTimeSelectConfigs();
        } finally {
            this.quickTimeSelectConfigLoading = false;
        }
    }

    async updateQuickTimeSelectConfigSort(newQuickTimeSelectConfigs: QuickTimeSelectConfig[]) {
        const configIds = newQuickTimeSelectConfigs.map(({ id }) => id);

        this.quickTimeSelectConfigs = await updateQuickTimeSelectConfigSort(configIds);
    }
}
